/**
 * Canonical client-side surface for global platform-owner capability.
 *
 * AUTHORITY LIVES IN THE DATABASE. Everything here is a thin, cached read of
 * `public.is_platform_owner()` plus typed wrappers over the platform RPCs.
 * Nothing in this module authorizes anything — the backend refuses privileged
 * calls from anyone else, and refuses *every* mutation while the active mode
 * is `diagnostic_view`. The client sandbox guard is a UX affordance only.
 */
import { supabase } from '@/integrations/supabase/client';
import { notifyPlatformSessionChanged } from '@/lib/platformOperate';

/** The one, nondelegable global platform owner. */
export const PLATFORM_OWNER_USER_ID = 'c5726759-9d13-424d-998e-2a307af1093e';

export type PlatformAccessMode =
  | 'platform_admin'
  | 'architect_participation'
  | 'diagnostic_view';

export const PLATFORM_ACCESS_MODES: readonly PlatformAccessMode[] = [
  'platform_admin',
  'architect_participation',
  'diagnostic_view',
] as const;

/**
 * Pure mirror of the backend rule in
 * `public.platform_assert_mutation_allowed`. Used only to disable controls
 * early and label the UI; the backend remains the authority.
 */
export function modeAllowsMutation(mode: PlatformAccessMode | null | undefined): boolean {
  return mode === 'platform_admin' || mode === 'architect_participation';
}

export function isReadOnlyMode(mode: PlatformAccessMode | null | undefined): boolean {
  return !modeAllowsMutation(mode);
}

/** Modes that require an explicit stated reason before the session opens. */
export function modeRequiresReason(mode: PlatformAccessMode): boolean {
  return mode === 'platform_admin' || mode === 'diagnostic_view';
}

export type PlatformInstanceStatus = 'active' | 'suspended' | 'pending_deletion';
export type PlatformInstanceStatusFilter = PlatformInstanceStatus | 'all';

export interface PlatformInstanceRow {
  id: string;
  name: string;
  slug: string | null;
  owner_user_id: string | null;
  tier: string | null;
  is_active: boolean;
  pending_deletion: boolean;
  pending_deletion_at: string | null;
  trial_ends_at: string | null;
  settings: Record<string, unknown> | null;
  status: PlatformInstanceStatus;
  created_at: string;
  member_count: number;
  project_count: number;
  total_count: number;
}

export interface PlatformInstancePage {
  rows: PlatformInstanceRow[];
  total: number;
}

export const PLATFORM_DISCOVERY_PAGE_SIZE = 25;
const PLATFORM_DISCOVERY_MAX_PAGE_SIZE = 200;

/** Mirror of the backend clamp so the client cannot request an unbounded page. */
export function clampDiscoveryParams(params: {
  limit?: number;
  offset?: number;
}): { limit: number; offset: number } {
  const rawLimit = Number.isFinite(params.limit) ? Number(params.limit) : PLATFORM_DISCOVERY_PAGE_SIZE;
  const rawOffset = Number.isFinite(params.offset) ? Number(params.offset) : 0;
  return {
    limit: Math.min(Math.max(Math.trunc(rawLimit), 1), PLATFORM_DISCOVERY_MAX_PAGE_SIZE),
    offset: Math.max(Math.trunc(rawOffset), 0),
  };
}

/** Mirror of `public.platform_instance_status`. */
export function deriveInstanceStatus(
  isActive: boolean | null | undefined,
  pendingDeletion: boolean | null | undefined,
): PlatformInstanceStatus {
  if (pendingDeletion) return 'pending_deletion';
  if (isActive) return 'active';
  return 'suspended';
}

// The platform RPCs ship with the (unapplied) platform-owner migration, so the
// generated Supabase types do not know them yet. This narrow escape hatch is
// deliberately local and typed at its boundary.
type UntypedRpc = (fn: string, args?: Record<string, unknown>) =>
  Promise<{ data: unknown; error: { message: string } | null }>;
const rawRpc = supabase.rpc.bind(supabase) as unknown as UntypedRpc;
// Read-only platform RPCs; every other call here (platform_*, invitation admin) can begin, end or
// replace an access session server-side (audited admin wrappers end the
// caller's session), so it signals shared invalidation once it settles,
// whether it succeeded or failed (the server state may have changed either way).
const PLATFORM_READ_RPCS = new Set(['is_platform_owner', 'platform_current_mode', 'platform_list_instances', 'platform_owner_ordinary_authority']);
const rpc: UntypedRpc = async (fn, args) => {
  const mutates = !PLATFORM_READ_RPCS.has(fn);
  try {
    return await rawRpc(fn, args);
  } finally {
    if (mutates) notifyPlatformSessionChanged();
  }
};

/** Canonical capability read. Fail-closed on any error. */
export async function fetchIsPlatformOwner(userId?: string | null): Promise<boolean> {
  if (userId === null) return false;
  const { data, error } = await rpc('is_platform_owner', userId ? { _user_id: userId } : {});
  if (error) return false;
  return data === true;
}

export async function fetchPlatformMode(): Promise<PlatformAccessMode | null> {
  const { data, error } = await rpc('platform_current_mode');
  if (error) return null;
  return (PLATFORM_ACCESS_MODES as readonly string[]).includes(String(data))
    ? (data as PlatformAccessMode)
    : null;
}

export async function beginPlatformSession(input: {
  mode: PlatformAccessMode;
  instanceId?: string | null;
  reason?: string | null;
  viewedUserId?: string | null;
  viewedRole?: string | null;
}): Promise<string> {
  if (modeRequiresReason(input.mode) && !input.reason?.trim()) {
    throw new Error('A reason is required for this mode.');
  }
  const { data, error } = await rpc('platform_begin_access_session', {
    _mode: input.mode,
    _instance_id: input.instanceId ?? null,
    _reason: input.reason ?? null,
    _viewed_user_id: input.viewedUserId ?? null,
    _viewed_role: input.viewedRole ?? null,
  });
  if (error) throw new Error(error.message);
  return String(data);
}

export async function endPlatformSession(sessionId?: string | null): Promise<void> {
  const { error } = await rpc('platform_end_access_session', { _session_id: sessionId ?? null });
  if (error) throw new Error(error.message);
}

/** Indexed, paginated, membership-independent global instance discovery. */
export async function listPlatformInstances(params: {
  search?: string | null;
  status?: PlatformInstanceStatusFilter;
  limit?: number;
  offset?: number;
} = {}): Promise<PlatformInstancePage> {
  const { limit, offset } = clampDiscoveryParams(params);
  const { data, error } = await rpc('platform_list_instances', {
    _search: params.search?.trim() || null,
    _status: params.status ?? 'all',
    _limit: limit,
    _offset: offset,
  });
  if (error) throw new Error(error.message);
  const rows = (Array.isArray(data) ? data : []) as PlatformInstanceRow[];
  return { rows, total: rows.length > 0 ? Number(rows[0].total_count) : 0 };
}

// ---------------------------------------------------------------------------
// Audited platform administration.
//
// These are the ONLY write paths the global management UI uses. Every one of
// them is refused by the database unless an active `platform_admin` session
// scoped to the exact tenant authorizes it, and the guard trigger on
// `instances` / `instance_members` refuses the equivalent direct table write.
// Nothing here is a security control — it is the client half of a boundary the
// backend enforces on its own.
// ---------------------------------------------------------------------------

/** Open a scoped `platform_admin` session, run one audited call, close it. */
async function withPlatformAdminSession<T>(
  instanceId: string,
  reason: string,
  run: () => Promise<T>,
): Promise<T> {
  const trimmed = reason?.trim() ?? '';
  if (!trimmed) throw new Error('A reason is required for platform administration.');
  const sessionId = await beginPlatformSession({
    mode: 'platform_admin',
    instanceId,
    reason: trimmed,
  });
  try {
    return await run();
  } finally {
    await endPlatformSession(sessionId).catch(() => undefined);
  }
}

async function callAudited(fn: string, args: Record<string, unknown>): Promise<void> {
  const { error } = await rpc(fn, args);
  if (error) throw new Error(error.message);
}

/** Audited team (User Group) RPCs. Backend: docs/migrations/platform-owner-team-administration.sql */
export type PlatformTeamRpc =
  | 'platform_create_team'
  | 'platform_update_team'
  | 'platform_delete_team'
  | 'platform_add_team_member'
  | 'platform_remove_team_member'
  | 'platform_add_team_manager'
  | 'platform_remove_team_manager';

/**
 * Run one audited team RPC inside a scoped platform_admin session for the
 * exact instance. Returns the RPC's JSON result (affected-row proof).
 */
export async function platformTeamCall(
  instanceId: string,
  reason: string,
  fn: PlatformTeamRpc,
  args: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return withPlatformAdminSession(instanceId, reason, async () => {
    const { data, error } = await rpc(fn, { _instance_id: instanceId, ...args, _reason: reason.trim() });
    if (error) throw new Error(error.message);
    if (!data || typeof data !== 'object') throw new Error('The change could not be confirmed.');
    return data as Record<string, unknown>;
  });
}

/**
 * Audited pending-invitation edit for the global platform owner: one scoped
 * platform_admin session for the invitation's own workspace, one
 * invitation_update_pending call, then cleanup. Returns the RPC's JSON result.
 * Backend: docs/migrations/pending-assignee-invitations.sql
 */
export async function platformUpdatePendingInvitation(input: {
  instanceId: string;
  args: Record<string, unknown>;
  reason: string;
}): Promise<{ data: unknown; error: { message: string } | null }> {
  return withPlatformAdminSession(input.instanceId, input.reason, () =>
    rpc('invitation_update_pending', { ...input.args, p_reason: input.reason.trim() }),
  );
}

/**
 * Audited pending-invitation revoke for the global platform owner: one scoped
 * platform_admin session for the invitation's own workspace, one
 * invitation_revoke_pending call, then cleanup.
 * Backend: docs/migrations/invitation-revoke-pending.sql
 */
export async function platformRevokePendingInvitation(input: {
  instanceId: string;
  args: Record<string, unknown>;
  reason: string;
}): Promise<{ data: unknown; error: { message: string } | null }> {
  return withPlatformAdminSession(input.instanceId, input.reason, () =>
    rpc('invitation_revoke_pending', { ...input.args, p_reason: input.reason.trim() }),
  );
}


/** Audited account-name RPC. Backend: docs/migrations/platform-member-account-name.sql */
export async function platformSetMemberAccountName(input: {
  instanceId: string;
  userId: string;
  name: string;
  reason: string;
}): Promise<{ user_id: string; display_name: string }> {
  return withPlatformAdminSession(input.instanceId, input.reason, async () => {
    const { data, error } = await rpc('platform_set_member_account_name', {
      _instance_id: input.instanceId,
      _user_id: input.userId,
      _display_name: input.name,
      _reason: input.reason.trim(),
    });
    if (error) throw new Error(error.message);
    if (!data || typeof data !== 'object') throw new Error('name_not_persisted');
    const r = data as Record<string, unknown>;
    return { user_id: String(r.user_id ?? ''), display_name: String(r.display_name ?? '') };
  });
}

export async function platformUpdateInstance(input: {
  instanceId: string;
  name: string;
  slug: string;
  settings: Record<string, unknown>;
  reason: string;
}): Promise<void> {
  await withPlatformAdminSession(input.instanceId, input.reason, () =>
    callAudited('platform_update_instance', {
      _instance_id: input.instanceId,
      _name: input.name,
      _slug: input.slug,
      _settings: input.settings,
      _reason: input.reason.trim(),
    }));
}

export async function platformSetInstanceActive(input: {
  instanceId: string;
  isActive: boolean;
  reason: string;
}): Promise<void> {
  await withPlatformAdminSession(input.instanceId, input.reason, () =>
    callAudited('platform_set_instance_active', {
      _instance_id: input.instanceId,
      _is_active: input.isActive,
      _reason: input.reason.trim(),
    }));
}

export async function platformReactivateInstance(input: {
  instanceId: string;
  reason: string;
}): Promise<void> {
  await withPlatformAdminSession(input.instanceId, input.reason, () =>
    callAudited('platform_reactivate_instance', {
      _instance_id: input.instanceId,
      _reason: input.reason.trim(),
    }));
}

export async function platformSetMemberRole(input: {
  instanceId: string;
  memberId: string;
  role: string;
  reason: string;
}): Promise<void> {
  await withPlatformAdminSession(input.instanceId, input.reason, () =>
    callAudited('platform_set_member_role', {
      _instance_id: input.instanceId,
      _member_id: input.memberId,
      _role: input.role,
      _reason: input.reason.trim(),
    }));
}

export async function platformDeactivateMember(input: {
  instanceId: string;
  memberId: string;
  reason: string;
}): Promise<void> {
  await withPlatformAdminSession(input.instanceId, input.reason, () =>
    callAudited('platform_deactivate_member', {
      _instance_id: input.instanceId,
      _member_id: input.memberId,
      _reason: input.reason.trim(),
    }));
}

// ---------------------------------------------------------------------------
// Member administration path selection.
//
// The shared member dialog keeps ONE behaviour for ordinary owners/admins: a
// direct table update, exactly as before. It switches to the audited platform
// RPCs only when the authenticated caller is the platform owner acting without
// ordinary authority in that tenant — the one case the direct write is (and
// must remain) refused by the backend guard.
// ---------------------------------------------------------------------------

export type MemberAdminPath = 'ordinary' | 'platform_audited';

/** Pure mirror of `public.platform_write_is_authorized`'s branch selection. */
export function resolveMemberAdminPath(input: {
  isPlatformOwner: boolean;
  hasOrdinaryAuthority: boolean;
}): MemberAdminPath {
  return input.isPlatformOwner && !input.hasOrdinaryAuthority ? 'platform_audited' : 'ordinary';
}

/** True when the caller holds ordinary tenant authority (instance owner/admin). */
export async function fetchPlatformOwnerOrdinaryAuthority(
  userId: string | null | undefined,
  instanceId: string | null | undefined,
): Promise<boolean> {
  if (!userId || !instanceId) return false;
  const { data, error } = await rpc('platform_owner_ordinary_authority', {
    _user_id: userId,
    _instance_id: instanceId,
  });
  if (error) return false;
  return data === true;
}

/**
 * Strict variants for group-administration path resolution: a failed lookup
 * THROWS instead of reading as "not the platform owner" / "no authority", so
 * a transient failure can never silently route the platform owner onto the
 * ordinary path (which the backend then refuses with a bare 403).
 */
export class AuthorityLookupError extends Error {
  constructor(which: string) {
    super(`authority_lookup_failed: ${which}`);
    this.name = 'AuthorityLookupError';
  }
}

export async function fetchIsPlatformOwnerStrict(userId: string): Promise<boolean> {
  const { data, error } = await rpc('is_platform_owner', { _user_id: userId });
  if (error || typeof data !== 'boolean') throw new AuthorityLookupError('is_platform_owner');
  return data;
}

export async function fetchPlatformOwnerOrdinaryAuthorityStrict(userId: string, instanceId: string): Promise<boolean> {
  const { data, error } = await rpc('platform_owner_ordinary_authority', {
    _user_id: userId,
    _instance_id: instanceId,
  });
  if (error || typeof data !== 'boolean') throw new AuthorityLookupError('platform_owner_ordinary_authority');
  return data;
}
